<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0 text-capitalize">
          {{
            (isAdd ? $t("actions.add") : $t("actions.edit")) +
            " " +
            $tc("tenants.title", 1) +
            " " +
            $t("tenants.store.storage_provider")
          }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- storage_provider -->
          <validation-provider
            #default="validationContext"
            :name="$t('tenants.store.storage_service')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('tenants.store.storage_service')"
              :label-for="$t('tenants.store.storage_service')"
            >
              <v-select
                v-model="itemData.storage_provider_id"
                :options="storageProviderOptions"
                @search="onSearch"
                :reduce="(val) => val.value"
                input-id="storage_service"
                :clearable="false"
                :disabled="disabledProvider"
              >
                <template #list-footer>
                  <li class="pagination">
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      :disabled="!hasPrevPage"
                      @click="prevPage"
                    >
                      <feather-icon icon="ChevronLeftIcon" size="18" />
                    </b-button>
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      :disabled="!hasNextPage"
                      @click="nextPage"
                    >
                      <feather-icon icon="ChevronRightIcon" size="18" />
                    </b-button>
                  </li>
                </template>
              </v-select>
              <b-form-invalid-feedback
                :state="getValidationState(validationContext)"
              >
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <b-button
            v-for="s in space"
            style="margin-bottom: 2px"
            :class="[selectedBtn == s.value ? 'btn-info' : 'btn-primary']"
            :key="s.value"
            :id="'btn-' + s.value"
            variant="primary"
            class="mr-2 text-capitalize btn btn-sm mb-1"
            @click="selectSpace(s.value)"
          >
            {{ $t("camera_proxy." + s.label) }}
          </b-button>

          <b-row v-if="custom" class="mt-1">
            <b-col cols="8">
              <validation-provider
                #default="validationContext"
                :name="$t('tenants.store.assigned_space')"
                rules="required"
              >
                <b-form-group
                  class="text-capitalize"
                  :label="$t('tenants.store.assigned_space')"
                  :label-for="$t('tenants.store.assigned_space')"
                >
                  <b-form-input
                    type="number"
                    step=".01"
                    id="description"
                    v-model="itemData.assigned_space"
                    :state="getValidationState(validationContext)"
                    trim
                  />
                  <b-form-invalid-feedback>
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
            <b-col cols="4">
              <validation-provider
                #default="validationContext"
                :name="$t('tenants.store.type')"
                rules="required"
              >
                <b-form-group
                  class="text-capitalize"
                  :label="$t('tenants.store.type')"
                  :label-for="$t('tenants.store.type')"
                >
                  <v-select
                    v-model="type_storage"
                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                    :options="storageSpace"
                    :reduce="(val) => val.value"
                    input-id="type_storage"
                    :clearable="false"
                  />
                  <b-form-invalid-feedback
                    :state="getValidationState(validationContext)"
                  >
                    {{ validationContext.errors[0] }}
                  </b-form-invalid-feedback>
                </b-form-group>
              </validation-provider>
            </b-col>
          </b-row>

          <validation-provider
            #default="validationContext"
            :name="$t('tenants.store.is_default')"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('tenants.store.is_default')"
              :label-for="$t('tenants.store.is_default')"
            >
              <b-form-checkbox
                id="checkbox-1"
                v-model="itemData.is_default"
                name="checkbox-1"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ $t("actions.save") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
  BImg,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref, computed, watch, onMounted } from "@vue/composition-api";
import { required } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { debounce } from "lodash";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import axios from "@axios";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormCheckbox,
    BImg,
    ValidationProvider,
    ValidationObserver,
  },
  directives: { Ripple },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: Boolean,
    itemEdit: Object,
    isAdd: Boolean,
    storageSpace: Array,
    tenantFilter: Number,
    tenantStorageProvider: Array,
    desabled: Boolean,
  },
  setup(props, context) {
    const toast = useToast();
    const { t } = useI18nUtils();
    const trans = {
      "messages.edit_save.successfully_created": t(
        "messages.edit_save.successfully_created"
      ),
      "messages.edit_save.updated_successfully": t(
        "messages.edit_save.updated_successfully"
      ),
    };

    const type_storage = ref("");
    const blankData = {
      tenant_id: "",
      storage_provider_id: "",
      assigned_space: "",
      is_default: false,
    };
    const itemData = ref({ ...blankData });

    const resetData = () => {
      itemData.value = { ...blankData };
    };

    const onSubmit = () => {
      let dispatch;
      if (type_storage.value === "gb") {
        itemData.value.assigned_space *= 1024;
      } else if (type_storage.value === "tb") {
        itemData.value.assigned_space *= 1024 * 1024;
      }
      itemData.value.tenant_id = props.tenantFilter;

      if (props.isAdd) {
        dispatch = store.dispatch(
          "tenant_storage_provider/add",
          itemData.value
        );
      } else {
        dispatch = store.dispatch("tenant_storage_provider/edit", {
          id: itemData.value.id,
          params: {
            tenant_id: props.tenantFilter,
            storage_provider_id: itemData.value.storage_provider_id,
            assigned_space: itemData.value.assigned_space,
            is_default: itemData.value.is_default,
          },
        });
      }

      dispatch
        .then((response) => {
          const msgKey = props.isAdd
            ? "messages.edit_save.successfully_created"
            : "messages.edit_save.updated_successfully";
          toast({
            component: ToastificationContent,
            props: {
              title: trans[msgKey],
              icon: "CheckIcon",
              variant: "success",
            },
          });
          context.emit("refetch-data");
          context.emit("update:is-sidebar-active", false);
        })
        .catch(() => {
          toast({
            component: ToastificationContent,
            props: {
              title: t("tenants.message.error"),
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetData);

    const disabledProvider = ref(false);
    const currentPage = ref(1);
    const search = ref("");
    const meta = ref(0);
    const limit = ref(8);
    const storageProviderOptions = ref([]);

    onMounted(fetchData);
    watch(currentPage, fetchData);

    function fetchData() {
      return axios
        .get(`${window.__env.VUE_APP_FILESYSTEM_URL}/v1/storage_provider`, {
          params: {
            name: search.value,
            per_page: limit.value,
            page: currentPage.value,
            order_by: "id",
            sortDesc: true,
          },
        })
        .then((res) => {
          meta.value = Math.ceil(res.data.total / limit.value);
          storageProviderOptions.value = res.data.data.map((p) => ({
            label: p.name,
            value: p.id,
          }));
          if (props.isAdd) filterProviders();
        })
        .catch(() => {
          console.error("Error fetching storage providers");
        });
    }

    function filterProviders() {
      storageProviderOptions.value = storageProviderOptions.value.filter(
        (opt) =>
          !props.tenantStorageProvider.some(
            (tsp) => tsp.storage_provider_id === opt.value
          )
      );
    }

    const space = ref([
      { label: "store.space_200_gb", value: 204800 },
      { label: "store.space_500_gb", value: 512000 },
      { label: "store.space_1_tb", value: 1048576 },
      { label: "limit.custom", value: 0 },
    ]);
    const custom = ref(true);
    const selectedBtn = ref(0);

    function selectSpace(val) {
      if (val === 0) {
        custom.value = true;
        itemData.value.assigned_space = null;
      } else {
        custom.value = false;
        itemData.value.assigned_space = val;
      }
      selectedBtn.value = val;
      type_storage.value = "";
    }

    function loadData() {
      type_storage.value = "";
      selectedBtn.value = 0;
      custom.value = true;
      disabledProvider.value = false;

      if (!props.isAdd) {
        disabledProvider.value = true;
        Object.assign(itemData.value, props.itemEdit);

        // Aseguramos que is_default sea booleano
        itemData.value.is_default = !!props.itemEdit.is_default;

        // Añadimos la opción si no existía
        if (
          !storageProviderOptions.value.some(
            (o) => o.value === props.itemEdit.storage_provider_id
          )
        ) {
          storageProviderOptions.value.push({
            label: props.itemEdit.storage_provider,
            value: props.itemEdit.storage_provider_id,
          });
        }

        // Conversión usando if … else if
        const mb = Number(props.itemEdit.assigned_space_mb);
        if (mb >= 1048576) {
          // ≥ 1 TB
          itemData.value.assigned_space = mb / 1048576;
          type_storage.value = "tb";
        } else if (mb >= 1024) {
          // ≥ 1 GB
          itemData.value.assigned_space = mb / 1024;
          type_storage.value = "gb";
        } else {
          // < 1 GB
          itemData.value.assigned_space = mb;
          type_storage.value = "mb";
        }

        // Marcamos el botón correspondiente si coincide con un valor predefinido
        const btn = space.value.find((s) => s.value === props.itemEdit.assigned_space_mb);
        if (btn) {
          selectedBtn.value = btn.value;
          custom.value = false;
        }
      } else {
        filterProviders();
      }
    }

    const hasNextPage = computed(() => currentPage.value < meta.value);
    const hasPrevPage = computed(() => currentPage.value > 1);
    const nextPage = () => ++currentPage.value;
    const prevPage = () => --currentPage.value;

    const onSearch = debounce((q, loading) => {
      loading(true);
      search.value = q;
      currentPage.value = 1;
      fetchData().finally(() => loading(false));
    }, 500);

    return {
      type_storage,
      itemData,
      space,
      custom,
      selectedBtn,
      storageProviderOptions,
      disabledProvider,
      isSidebarActive: props.isSidebarActive,
      isAdd: props.isAdd,
      onSubmit,
      loadData,
      resetForm,
      refFormObserver,
      getValidationState,
      selectSpace,
      hasNextPage,
      hasPrevPage,
      nextPage,
      prevPage,
      onSearch,
    };
  },
};
</script>

<style lang="scss">
.pagination {
  display: flex;
  justify-content: space-around;
  margin: 0.25rem 0.25rem 0;
}
.pagination button {
  flex-grow: 1;
  &:first-child {
    margin-right: 5px;
  }
}
.pagination button:hover {
  cursor: pointer;
}
</style>
